import { Button, ButtonVariants, FormControlSizes, HStack, IconButton, IconName, type ColumnDef } from '@talos/kyoko';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import type { ManualRFQ } from './types';
import { getManualRFQActions } from './utils';

export interface UseManualRFQActionColumnParams {
  onFixClick: (quote: ManualRFQ) => void;
  onQuoteClick: (quote: ManualRFQ) => void;
  onShowQuoteClick: (quote: ManualRFQ) => void;
}

export const useManualRFQActionColumn = ({
  onFixClick,
  onQuoteClick,
  onShowQuoteClick,
}: UseManualRFQActionColumnParams) => {
  const column: ColumnDef<ManualRFQ> = useMemo(() => {
    return {
      type: 'custom',
      id: 'action',
      title: 'Actions',
      exportable: false, // make sure the column doesn't show up in exports
      width: 100,
      maxWidth: 100,
      minWidth: 100,

      params: {
        suppressColumnsToolPanel: true, // dont render as a selectable item in the column chooser
        suppressAutoSize: true,
        sortable: false,
        resizable: false,
        pinned: 'right',
        lockVisible: true,
        lockPosition: true,
        lockPinned: true,
        // The entire data object is our value in this column. We want to re-render on every data update.
        valueGetter: (params: ValueGetterParams<ManualRFQ>): ManualRFQ | undefined => {
          return params.data;
        },
        cellRenderer: (params: ICellRendererParams<ManualRFQ, ManualRFQ>) => {
          const quote = params.data;
          if (!quote) {
            return null;
          }

          const actionItems = getManualRFQActions(quote);
          return (
            <HStack gap="spacing6" w="100%" justifyContent="flex-start">
              <IconButton
                icon={IconName.Deepdive}
                size={FormControlSizes.Small}
                dim
                flex="0"
                onClick={() => onShowQuoteClick(quote)}
                data-testid="manual-rfq-action-button-show"
              />
              {actionItems.map(item => {
                /* If we start introducing more actions we can create an abstraction here */
                if (item.type === 'fix') {
                  return (
                    <Button
                      key={item.type}
                      disabled={item.disabled}
                      onClick={() => onFixClick(quote)}
                      variant={ButtonVariants.Primary}
                      size={FormControlSizes.Small}
                      flex="1"
                      data-testid="manual-rfq-action-button-fix"
                    >
                      Fix
                    </Button>
                  );
                }
                if (item.type === 'quote') {
                  return (
                    <Button
                      key={item.type}
                      disabled={item.disabled}
                      onClick={() => onQuoteClick(quote)}
                      variant={ButtonVariants.Primary}
                      size={FormControlSizes.Small}
                      flex="1"
                      data-testid="manual-rfq-action-button-quote"
                    >
                      Quote
                    </Button>
                  );
                }
                return null; // unexpected type
              })}
            </HStack>
          );
        },
      } satisfies ColDef,
    };
  }, [onFixClick, onQuoteClick, onShowQuoteClick]);

  return column;
};
